import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import type { Service } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";

interface FieldDraft {
  id?: string;
  label: string;
  helpText: string;
  kind: string;
  options: string;
  required: boolean;
}

interface AddonDraft {
  id?: string;
  label: string;
  description: string;
  priceDollars: string;
}

interface Draft {
  title: string;
  description: string;
  startingPriceDollars: string;
  currency: string;
  deliveryDays: string;
  revisionRounds: string;
  revisionPolicy: string;
  quoteMode: "instant" | "manual";
  depositPercent: string;
  isActive: boolean;
  fields: FieldDraft[];
  addons: AddonDraft[];
}

const empty: Draft = {
  title: "",
  description: "",
  startingPriceDollars: "60",
  currency: "USD",
  deliveryDays: "7",
  revisionRounds: "2",
  revisionPolicy: "Two minor revisions included. Major composition changes after sketch approval are billed separately.",
  quoteMode: "instant",
  depositPercent: "50",
  isActive: true,
  fields: [
    { label: "Character name", helpText: "", kind: "text", options: "", required: true },
    { label: "Style", helpText: "Pick the look you want.", kind: "select", options: "Clean lineart, Painterly, Pixel", required: true },
    { label: "References", helpText: "Upload visual refs, mood, etc.", kind: "upload", options: "", required: false },
    { label: "NSFW", helpText: "Only enable if applicable.", kind: "boolean", options: "", required: false },
  ],
  addons: [
    { label: "Commercial usage", description: "License for commercial use.", priceDollars: "80" },
    { label: "Source files", description: "Layered source PSD/AI.", priceDollars: "40" },
  ],
};

function toCents(dollars: string) {
  const n = Number(dollars);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

function fromCents(c: number) {
  return (c / 100).toFixed(0);
}

export function ServiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const [draft, setDraft] = useState<Draft>(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { service } = await api.get<{ service: Service }>(`/api/services/${id}`);
        setDraft({
          title: service.title,
          description: service.description ?? "",
          startingPriceDollars: fromCents(service.starting_price_cents),
          currency: service.currency,
          deliveryDays: String(service.delivery_days),
          revisionRounds: String(service.revision_rounds),
          revisionPolicy: service.revision_policy ?? "",
          quoteMode: service.quote_mode,
          depositPercent: String(service.deposit_percent),
          isActive: !!service.is_active,
          fields: (service.fields ?? []).map(f => ({
            id: f.id,
            label: f.label,
            helpText: f.help_text ?? "",
            kind: f.kind,
            options: f.options_json ? (JSON.parse(f.options_json) as string[]).join(", ") : "",
            required: !!f.required,
          })),
          addons: (service.addons ?? []).map(a => ({
            id: a.id,
            label: a.label,
            description: a.description ?? "",
            priceDollars: fromCents(a.price_cents),
          })),
        });
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : "load failed");
      } finally { setLoading(false); }
    })();
  }, [id, isNew]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: draft.title,
      description: draft.description || null,
      startingPriceCents: toCents(draft.startingPriceDollars),
      currency: draft.currency,
      deliveryDays: Number(draft.deliveryDays) || 7,
      revisionRounds: Number(draft.revisionRounds) || 0,
      revisionPolicy: draft.revisionPolicy || null,
      quoteMode: draft.quoteMode,
      depositPercent: Number(draft.depositPercent) || 50,
      isActive: draft.isActive,
      fields: draft.fields.map(f => ({
        id: f.id,
        label: f.label,
        helpText: f.helpText || null,
        kind: f.kind,
        options: f.options ? f.options.split(",").map(s => s.trim()).filter(Boolean) : null,
        required: f.required,
      })),
      addons: draft.addons.map(a => ({
        id: a.id,
        label: a.label,
        description: a.description || null,
        priceCents: toCents(a.priceDollars),
      })),
    };
    try {
      if (isNew) {
        const res = await api.post<{ service: Service }>("/api/services", payload);
        toast.success("Service published");
        nav(`/studio/services/${res.service.id}`);
      } else {
        await api.patch(`/api/services/${id}`, payload);
        toast.success("Saved");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "save failed");
    } finally { setSaving(false); }
  }

  async function onDelete() {
    if (!id || isNew) return;
    if (!confirm("Hide this service from your page?")) return;
    await api.del(`/api/services/${id}`);
    nav("/studio");
  }

  if (loading) return <div className="h-96 animate-pulse rounded bg-muted" />;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            <Link to="/studio" className="underline underline-offset-4">Studio</Link> / service
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">
            {isNew ? "New service" : draft.title || "Untitled"}
          </h1>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button type="button" variant="outline" onClick={onDelete}>
              <Trash2 className="mr-1 h-4 w-4" /> Delete
            </Button>
          )}
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : isNew ? "Publish" : "Save"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Basics</CardTitle></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2 space-y-1.5">
            <Label>Title</Label>
            <Input required value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="Portrait commission" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={3} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="A short pitch — what this is, style, canvas size, etc." />
          </div>
          <div className="space-y-1.5">
            <Label>Starting price</Label>
            <div className="flex gap-2">
              <Input value={draft.startingPriceDollars} onChange={e => setDraft({ ...draft, startingPriceDollars: e.target.value })} />
              <Select value={draft.currency} onValueChange={v => setDraft({ ...draft, currency: v })}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["USD", "EUR", "GBP", "CAD", "AUD", "JPY"].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Delivery (days)</Label>
            <Input type="number" min={1} value={draft.deliveryDays} onChange={e => setDraft({ ...draft, deliveryDays: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Revision rounds</Label>
            <Input type="number" min={0} value={draft.revisionRounds} onChange={e => setDraft({ ...draft, revisionRounds: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Deposit %</Label>
            <Input type="number" min={0} max={100} value={draft.depositPercent} onChange={e => setDraft({ ...draft, depositPercent: e.target.value })} />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label>Revision policy</Label>
            <Textarea rows={2} value={draft.revisionPolicy} onChange={e => setDraft({ ...draft, revisionPolicy: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Quote mode</Label>
            <Select value={draft.quoteMode} onValueChange={v => setDraft({ ...draft, quoteMode: v as "instant" | "manual" })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="instant">Instant — buyer pays deposit immediately</SelectItem>
                <SelectItem value="manual">Manual — you set a quote after reviewing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={draft.isActive} onCheckedChange={v => setDraft({ ...draft, isActive: !!v })} />
            Visible on your public page
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">Intake fields</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setDraft(d => ({
                ...d,
                fields: [...d.fields, { label: "", helpText: "", kind: "text", options: "", required: false }],
              }))
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Add field
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {draft.fields.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Add the exact fields you need from a buyer. Saves the back-and-forth later.
            </p>
          )}
          {draft.fields.map((f, i) => (
            <div key={i} className="rounded-md border p-3">
              <div className="flex items-start gap-2">
                <GripVertical className="mt-3 h-4 w-4 text-muted-foreground" />
                <div className="grid flex-1 gap-3 md:grid-cols-4">
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs">Label</Label>
                    <Input
                      value={f.label}
                      onChange={e => {
                        const fields = [...draft.fields]; fields[i] = { ...f, label: e.target.value };
                        setDraft({ ...draft, fields });
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Kind</Label>
                    <Select value={f.kind} onValueChange={v => {
                      const fields = [...draft.fields]; fields[i] = { ...f, kind: v };
                      setDraft({ ...draft, fields });
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["text", "textarea", "number", "select", "multiselect", "boolean", "date", "upload"].map(k =>
                          <SelectItem key={k} value={k}>{k}</SelectItem>,
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <Checkbox
                      checked={f.required}
                      onCheckedChange={v => {
                        const fields = [...draft.fields]; fields[i] = { ...f, required: !!v };
                        setDraft({ ...draft, fields });
                      }}
                    />
                    Required
                  </label>
                  {(f.kind === "select" || f.kind === "multiselect") && (
                    <div className="space-y-1 md:col-span-4">
                      <Label className="text-xs">Options (comma-separated)</Label>
                      <Input value={f.options} onChange={e => {
                        const fields = [...draft.fields]; fields[i] = { ...f, options: e.target.value };
                        setDraft({ ...draft, fields });
                      }} />
                    </div>
                  )}
                  <div className="space-y-1 md:col-span-4">
                    <Label className="text-xs">Help text</Label>
                    <Input value={f.helpText} onChange={e => {
                      const fields = [...draft.fields]; fields[i] = { ...f, helpText: e.target.value };
                      setDraft({ ...draft, fields });
                    }} />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setDraft({ ...draft, fields: draft.fields.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">Add-ons</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setDraft(d => ({
                ...d,
                addons: [...d.addons, { label: "", description: "", priceDollars: "0" }],
              }))
            }
          >
            <Plus className="mr-1 h-4 w-4" /> Add add-on
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {draft.addons.length === 0 && (
            <p className="text-sm text-muted-foreground">Priced options the buyer can toggle on (commercial rights, source files, etc.).</p>
          )}
          {draft.addons.map((a, i) => (
            <div key={i} className="flex items-start gap-2 rounded-md border p-3">
              <div className="grid flex-1 gap-3 md:grid-cols-4">
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs">Label</Label>
                  <Input value={a.label} onChange={e => {
                    const addons = [...draft.addons]; addons[i] = { ...a, label: e.target.value };
                    setDraft({ ...draft, addons });
                  }} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Price</Label>
                  <Input value={a.priceDollars} onChange={e => {
                    const addons = [...draft.addons]; addons[i] = { ...a, priceDollars: e.target.value };
                    setDraft({ ...draft, addons });
                  }} />
                </div>
                <div className="space-y-1 md:col-span-4">
                  <Label className="text-xs">Description</Label>
                  <Input value={a.description} onChange={e => {
                    const addons = [...draft.addons]; addons[i] = { ...a, description: e.target.value };
                    setDraft({ ...draft, addons });
                  }} />
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setDraft({ ...draft, addons: draft.addons.filter((_, j) => j !== i) })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Separator />
      <div className="flex justify-end gap-2">
        <Button asChild variant="outline" type="button"><Link to="/studio">Cancel</Link></Button>
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : isNew ? "Publish" : "Save"}</Button>
      </div>
    </form>
  );
}
